// ===========================================
// Custom SMS & WhatsApp Editor Page
// ===========================================
// Admin interface for creating and editing SMS/WhatsApp campaigns

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTenant } from '../../contexts/TenantContext';
import {
  Box,
  Paper,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Autocomplete,
  Chip,
  Divider,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  ArrowBack as BackIcon,
  Sms as SmsIcon,
  WhatsApp as WhatsAppIcon,
  People as PeopleIcon,
  Phone as TestIcon,
  MergeType as MergeIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import {
  useCustomSmsCampaign,
  useCreateCustomSmsCampaign,
  useUpdateCustomSmsCampaign,
  useScheduleCustomSmsCampaign,
  useSendCustomSmsCampaign,
  useSendTestSms,
  useSmsCampaignRecipients,
} from '../../hooks/useCustomSms';
import {
  useAgeGroups,
  useProgramLevels,
  useInstruments,
} from '../../hooks/useAdmin';
import {
  CreateCustomSmsCampaignData,
  UpdateCustomSmsCampaignData,
  CustomSmsStatus,
  CustomSmsChannel,
} from '../../types/customSms.types';
import { calculateSmsCharacterCount } from '../../utils/smsCharacterCount';

// ===========================================
// MERGE FIELDS
// ===========================================

const MERGE_FIELDS = [
  { label: 'First Name', value: '{{firstName}}' },
  { label: 'Last Name', value: '{{lastName}}' },
  { label: 'School Name', value: '{{schoolName}}' },
];

// ===========================================
// CHARACTER COUNTER
// ===========================================

interface CharacterCounterProps {
  body: string;
  channel: CustomSmsChannel;
}

function CharacterCounter({ body, channel }: CharacterCounterProps) {
  const result = useMemo(() => calculateSmsCharacterCount(body), [body]);

  if (channel === 'WHATSAPP') {
    return (
      <Typography variant="caption" color="text.secondary">
        {body.length} characters
        {body.length > 1600 && (
          <Typography component="span" color="error.main"> (exceeds 1600 char WhatsApp limit)</Typography>
        )}
      </Typography>
    );
  }

  const segmentColor =
    result.segmentCount >= 4
      ? 'error.main'
      : result.segmentCount >= 2
        ? 'warning.main'
        : 'text.secondary';

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Typography variant="caption" color="text.secondary">
        {result.characterCount} chars
        {!result.isGsm7 && (
          <Tooltip title="Message contains non-GSM characters, switching to UCS-2 encoding">
            <Typography component="span" color="warning.main" sx={{ ml: 0.5 }}>
              (UCS-2)
            </Typography>
          </Tooltip>
        )}
      </Typography>
      <Typography variant="caption" color={segmentColor}>
        {result.segmentCount} segment{result.segmentCount !== 1 ? 's' : ''}
        {result.segmentCount > 1 && ` (${result.remainingInSegment} remaining)`}
      </Typography>
      {result.segmentCount > 1 && (
        <LinearProgress
          variant="determinate"
          value={((result.multiSmsSegmentLimit - result.remainingInSegment) / result.multiSmsSegmentLimit) * 100}
          color={result.segmentCount >= 4 ? 'error' : result.segmentCount >= 2 ? 'warning' : 'primary'}
          sx={{ flex: 1, height: 4, borderRadius: 1 }}
        />
      )}
    </Stack>
  );
}

// ===========================================
// SCHEDULE DIALOG
// ===========================================

interface ScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  onSchedule: (date: string) => void;
  isScheduling: boolean;
}

function ScheduleDialog({ open, onClose, onSchedule, isScheduling }: ScheduleDialogProps) {
  const [scheduledFor, setScheduledFor] = useState('');

  const handleSchedule = () => {
    if (scheduledFor) {
      onSchedule(new Date(scheduledFor).toISOString());
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Schedule Campaign</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose when to send this campaign. Make sure to save any changes first.
        </Typography>
        <TextField
          label="Schedule For"
          type="datetime-local"
          value={scheduledFor}
          onChange={(e) => setScheduledFor(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: new Date().toISOString().slice(0, 16) }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isScheduling}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSchedule}
          disabled={isScheduling || !scheduledFor}
        >
          {isScheduling ? <CircularProgress size={20} /> : 'Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// TEST MESSAGE DIALOG
// ===========================================

interface TestSmsDialogProps {
  open: boolean;
  onClose: () => void;
  onSend: (phone: string) => void;
  isSending: boolean;
}

function TestSmsDialog({ open, onClose, onSend, isSending }: TestSmsDialogProps) {
  const [phone, setPhone] = useState('');

  const handleSend = () => {
    if (phone) {
      onSend(phone);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Send Test Message</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Send a preview of this message to a phone number to see how it will appear.
        </Typography>
        <TextField
          label="Phone Number"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          fullWidth
          placeholder="+61400000000"
          helperText="Enter phone number in E.164 format (e.g. +61400000000)"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={isSending || !phone.trim()}
        >
          {isSending ? <CircularProgress size={20} /> : 'Send Test'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// RECIPIENT PREVIEW DIALOG
// ===========================================

interface RecipientPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  campaignId: string;
  channel: CustomSmsChannel;
}

function RecipientPreviewDialog({ open, onClose, campaignId, channel }: RecipientPreviewDialogProps) {
  const { data, isLoading, isError } = useSmsCampaignRecipients(campaignId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Recipient Preview</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Alert severity="error">Failed to load recipient preview.</Alert>
        ) : data ? (
          <Stack spacing={2}>
            <Typography variant="h6">Total: {data.total} recipients</Typography>
            <Stack direction="row" spacing={1}>
              <Chip label={`Parents: ${data.breakdown.byType.parents}`} color="primary" variant="outlined" size="small" />
              <Chip label={`Teachers: ${data.breakdown.byType.teachers}`} color="secondary" variant="outlined" size="small" />
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {(channel === 'SMS' || channel === 'BOTH') && (
                <Chip
                  icon={<SmsIcon />}
                  label={`SMS eligible: ${data.breakdown.bySmsEligible}`}
                  color="primary"
                  variant="outlined"
                />
              )}
              {(channel === 'WHATSAPP' || channel === 'BOTH') && (
                <Chip
                  icon={<WhatsAppIcon />}
                  label={`WhatsApp eligible: ${data.breakdown.byWhatsAppEligible}`}
                  color="success"
                  variant="outlined"
                />
              )}
              {channel === 'BOTH' && data.breakdown.byWhatsAppEligible > 0 && (
                <Chip
                  label={`WA + SMS: ${data.breakdown.byWhatsAppEligible}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
            {data.recipients.slice(0, 10).length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Sample (first 10):
                </Typography>
                <Stack spacing={0.5}>
                  {data.recipients.slice(0, 10).map((r) => (
                    <Typography key={r.userId} variant="body2">
                      {r.firstName} {r.lastName} — {r.phone} ({r.recipientType})
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        ) : (
          <Typography color="text.secondary">No recipients found for current targeting.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function CustomSmsEditorPage() {
  const navigate = useNavigate();
  const { schoolSlug } = useTenant();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const { enqueueSnackbar } = useSnackbar();

  // Form state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [channel, setChannel] = useState<CustomSmsChannel>('SMS');
  const [sendToAll, setSendToAll] = useState(true);
  const [targetTeachers, setTargetTeachers] = useState(false);
  const [targetAgeGroups, setTargetAgeGroups] = useState<string[]>([]);
  const [targetProgramLevels, setTargetProgramLevels] = useState<string[]>([]);
  const [targetInstruments, setTargetInstruments] = useState<string[]>([]);

  // Dialog state
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [testSmsOpen, setTestSmsOpen] = useState(false);
  const [recipientPreviewOpen, setRecipientPreviewOpen] = useState(false);

  // Queries and mutations
  const { data: campaign, isLoading } = useCustomSmsCampaign(id || '');
  const createMutation = useCreateCustomSmsCampaign();
  const updateMutation = useUpdateCustomSmsCampaign();
  const scheduleMutation = useScheduleCustomSmsCampaign();
  const sendMutation = useSendCustomSmsCampaign();
  const testSmsMutation = useSendTestSms();

  // Targeting options
  const { data: ageGroupsData } = useAgeGroups();
  const { data: programLevelsData } = useProgramLevels();
  const { data: instrumentsData } = useInstruments();

  const ageGroupOptions = ageGroupsData?.map((ag) => ag.name) || [];
  const programLevelOptions = programLevelsData?.map((pl) => pl.name) || [];
  const instrumentOptions = instrumentsData?.map((i) => i.name) || [];

  // Load campaign data into form
  useEffect(() => {
    if (campaign) {
      setTitle(campaign.title);
      setBody(campaign.body);
      setChannel(campaign.channel);
      setSendToAll(campaign.sendToAll);
      setTargetTeachers(campaign.targetTeachers);
      setTargetAgeGroups(campaign.targetAgeGroups);
      setTargetProgramLevels(campaign.targetProgramLevels);
      setTargetInstruments(campaign.targetInstruments);
    }
  }, [campaign]);

  const handleInsertMergeField = (value: string) => {
    const textarea = document.getElementById('sms-body') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newBody = body.substring(0, start) + value + body.substring(end);
      setBody(newBody);
      setTimeout(() => {
        textarea.selectionStart = start + value.length;
        textarea.selectionEnd = start + value.length;
        textarea.focus();
      }, 0);
    } else {
      setBody((prev) => prev + value);
    }
  };

  const handleSave = async () => {
    try {
      const data: CreateCustomSmsCampaignData | UpdateCustomSmsCampaignData = {
        title,
        body,
        channel,
        sendToAll,
        targetTeachers,
        targetAgeGroups,
        targetProgramLevels,
        targetInstruments,
      };

      if (isNew) {
        const created = await createMutation.mutateAsync(data as CreateCustomSmsCampaignData);
        enqueueSnackbar('Campaign created successfully', { variant: 'success' });
        navigate(`/${schoolSlug}/admin/custom-sms/${created.id}`, { replace: true });
      } else if (id) {
        await updateMutation.mutateAsync({ campaignId: id, data: data as UpdateCustomSmsCampaignData });
        enqueueSnackbar('Campaign saved successfully', { variant: 'success' });
      }
    } catch (err) {
      console.error('Failed to save campaign:', err);
      enqueueSnackbar('Failed to save campaign', { variant: 'error' });
    }
  };

  const handleSchedule = async (scheduledFor: string) => {
    if (!id) return;
    try {
      await scheduleMutation.mutateAsync({ campaignId: id, data: { scheduledFor } });
      setScheduleOpen(false);
      enqueueSnackbar('Campaign scheduled successfully', { variant: 'success' });
    } catch (err) {
      console.error('Failed to schedule campaign:', err);
      enqueueSnackbar('Failed to schedule campaign', { variant: 'error' });
    }
  };

  const handleSend = async () => {
    if (!id) return;
    try {
      await sendMutation.mutateAsync(id);
      enqueueSnackbar('Campaign is being sent', { variant: 'success' });
    } catch (err) {
      console.error('Failed to send campaign:', err);
    }
  };

  const handleTestSms = async (phone: string) => {
    if (!id) return;
    try {
      await testSmsMutation.mutateAsync({ campaignId: id, data: { phone } });
      setTestSmsOpen(false);
      enqueueSnackbar('Test message sent', { variant: 'success' });
    } catch (err) {
      console.error('Failed to send test message:', err);
      enqueueSnackbar('Failed to send test message', { variant: 'error' });
    }
  };

  if (!isNew && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const status: CustomSmsStatus = campaign?.status || 'DRAFT';
  const canSchedule = status === 'DRAFT' && !isNew;
  const canSend = status === 'DRAFT' && !isNew;
  const canTest = !isNew;
  const isReadOnly = status === 'SENT' || status === 'SENDING';

  return (
    <Box>
      <PageHeader
        title={isNew ? 'Create Campaign' : 'Edit Campaign'}
        subtitle={campaign?.title || 'New SMS/WhatsApp campaign'}
        action={
          <Stack direction="row" spacing={1}>
            <Button startIcon={<BackIcon />} component={Link} to={`/${schoolSlug}/admin/custom-sms`}>
              Back
            </Button>
            {canTest && (
              <Button
                startIcon={<TestIcon />}
                onClick={() => setTestSmsOpen(true)}
                disabled={testSmsMutation.isPending}
              >
                Test
              </Button>
            )}
            {canSchedule && (
              <Button
                startIcon={<ScheduleIcon />}
                onClick={() => setScheduleOpen(true)}
                disabled={scheduleMutation.isPending}
              >
                Schedule
              </Button>
            )}
            {canSend && (
              <Button
                variant="outlined"
                color="success"
                startIcon={<SendIcon />}
                onClick={handleSend}
                disabled={sendMutation.isPending}
              >
                Send Now
              </Button>
            )}
            {!isReadOnly && (
              <Button
                variant="contained"
                startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={isSaving || !title.trim() || !body.trim()}
              >
                Save
              </Button>
            )}
          </Stack>
        }
      />

      {isNew && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Save your campaign first to enable testing, scheduling, and sending options.
        </Alert>
      )}

      {isReadOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This campaign has been sent and cannot be edited. View the{' '}
          <Link to={`/${schoolSlug}/admin/custom-sms/${id}/analytics`}>analytics page</Link> for
          delivery stats.
        </Alert>
      )}

      {createMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to create campaign:{' '}
          {createMutation.error instanceof Error ? createMutation.error.message : 'Unknown error'}
        </Alert>
      )}
      {updateMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to update campaign:{' '}
          {updateMutation.error instanceof Error ? updateMutation.error.message : 'Unknown error'}
        </Alert>
      )}
      {sendMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to send campaign:{' '}
          {sendMutation.error instanceof Error ? sendMutation.error.message : 'Unknown error'}
        </Alert>
      )}
      {sendMutation.isSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Campaign is being distributed to recipients!
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        {/* Main content */}
        <Box sx={{ flex: 2 }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Campaign Details
            </Typography>
            <Stack spacing={3}>
              <TextField
                label="Internal Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                required
                disabled={isReadOnly}
                placeholder="Internal name for this campaign (not sent to recipients)"
                helperText="This is for your reference only"
              />

              {/* Channel selector */}
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Channel
                </Typography>
                <ToggleButtonGroup
                  value={channel}
                  exclusive
                  onChange={(_, v) => { if (v) setChannel(v); }}
                  disabled={isReadOnly}
                  size="small"
                >
                  <ToggleButton value="SMS">
                    <SmsIcon fontSize="small" sx={{ mr: 0.5 }} /> SMS
                  </ToggleButton>
                  <ToggleButton value="WHATSAPP">
                    <WhatsAppIcon fontSize="small" sx={{ mr: 0.5 }} /> WhatsApp
                  </ToggleButton>
                  <ToggleButton value="BOTH">
                    <SmsIcon fontSize="small" sx={{ mr: 0.5 }} />
                    <WhatsAppIcon fontSize="small" sx={{ mr: 0.5 }} /> Both
                  </ToggleButton>
                </ToggleButtonGroup>
                {channel === 'BOTH' && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    WhatsApp will be tried first; SMS is used as fallback for recipients without WhatsApp enabled.
                  </Typography>
                )}
              </Box>
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Message</Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {MERGE_FIELDS.map((field) => (
                  <Chip
                    key={field.value}
                    label={field.label}
                    size="small"
                    icon={<MergeIcon />}
                    onClick={() => handleInsertMergeField(field.value)}
                    disabled={isReadOnly}
                    clickable
                    variant="outlined"
                    color="primary"
                  />
                ))}
              </Stack>
            </Stack>

            <TextField
              id="sms-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              fullWidth
              multiline
              rows={6}
              disabled={isReadOnly}
              placeholder="Type your message here. Use the merge field chips above to personalise."
              inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.875rem' } }}
            />

            <Box sx={{ mt: 1 }}>
              <CharacterCounter body={body} channel={channel} />
            </Box>

            {channel !== 'WHATSAPP' && (
              <Alert severity="info" sx={{ mt: 2 }} icon={false}>
                <Typography variant="caption">
                  <strong>GSM-7</strong>: standard characters — 160 chars per SMS, 153 per segment in multi-part messages.{' '}
                  <strong>UCS-2</strong>: emoji/special chars — 70 chars per SMS, 67 per segment.
                </Typography>
              </Alert>
            )}
          </Paper>
        </Box>

        {/* Sidebar */}
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Targeting
            </Typography>
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={sendToAll}
                    onChange={(e) => setSendToAll(e.target.checked)}
                    disabled={isReadOnly}
                  />
                }
                label="Send to all parents"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={targetTeachers}
                    onChange={(e) => setTargetTeachers(e.target.checked)}
                    disabled={isReadOnly}
                  />
                }
                label="Include teachers"
              />

              {!sendToAll && (
                <>
                  <Divider />
                  <Typography variant="subtitle2" color="text.secondary">
                    Target specific groups:
                  </Typography>

                  <Autocomplete
                    multiple
                    options={ageGroupOptions}
                    value={targetAgeGroups}
                    onChange={(_, value) => setTargetAgeGroups(value)}
                    disabled={isReadOnly}
                    renderInput={(params) => (
                      <TextField {...params} label="Age Groups" size="small" />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                      ))
                    }
                  />

                  <Autocomplete
                    multiple
                    options={programLevelOptions}
                    value={targetProgramLevels}
                    onChange={(_, value) => setTargetProgramLevels(value)}
                    disabled={isReadOnly}
                    renderInput={(params) => (
                      <TextField {...params} label="Program Levels" size="small" />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                      ))
                    }
                  />

                  <Autocomplete
                    multiple
                    options={instrumentOptions}
                    value={targetInstruments}
                    onChange={(_, value) => setTargetInstruments(value)}
                    disabled={isReadOnly}
                    renderInput={(params) => (
                      <TextField {...params} label="Instruments" size="small" />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                      ))
                    }
                  />
                </>
              )}

              {!isNew && (
                <Button
                  variant="outlined"
                  startIcon={<PeopleIcon />}
                  onClick={() => setRecipientPreviewOpen(true)}
                  fullWidth
                >
                  Preview Recipients
                </Button>
              )}
            </Stack>
          </Paper>

          {/* Status card */}
          {campaign && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Status
              </Typography>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={status}
                    size="small"
                    color={
                      status === 'SENT'
                        ? 'success'
                        : status === 'SENDING'
                          ? 'warning'
                          : status === 'SCHEDULED'
                            ? 'info'
                            : status === 'FAILED'
                              ? 'error'
                              : 'default'
                    }
                  />
                </Stack>
                {campaign.scheduledFor && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Scheduled For
                    </Typography>
                    <Typography variant="body2">
                      {new Date(campaign.scheduledFor).toLocaleString()}
                    </Typography>
                  </Stack>
                )}
                {campaign.sentAt && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Sent At
                    </Typography>
                    <Typography variant="body2">
                      {new Date(campaign.sentAt).toLocaleString()}
                    </Typography>
                  </Stack>
                )}
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Recipients
                  </Typography>
                  <Typography variant="body2">{campaign.totalRecipients}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Sent
                  </Typography>
                  <Typography variant="body2">{campaign.sentCount}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Delivered
                  </Typography>
                  <Typography variant="body2">{campaign.deliveredCount}</Typography>
                </Stack>
                {campaign.failedCount > 0 && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Failed
                    </Typography>
                    <Typography variant="body2" color="error.main">
                      {campaign.failedCount}
                    </Typography>
                  </Stack>
                )}
                {campaign.fallbackCount > 0 && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Fallback (SMS)
                    </Typography>
                    <Typography variant="body2">{campaign.fallbackCount}</Typography>
                  </Stack>
                )}
                {(status === 'SENT' || status === 'SENDING') && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SmsIcon />}
                    component={Link}
                    to={`/${schoolSlug}/admin/custom-sms/${id}/analytics`}
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    View Analytics
                  </Button>
                )}
              </Stack>
            </Paper>
          )}
        </Box>
      </Stack>

      {/* Dialogs */}
      <ScheduleDialog
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        onSchedule={handleSchedule}
        isScheduling={scheduleMutation.isPending}
      />

      <TestSmsDialog
        open={testSmsOpen}
        onClose={() => setTestSmsOpen(false)}
        onSend={handleTestSms}
        isSending={testSmsMutation.isPending}
      />

      {id && id !== 'new' && (
        <RecipientPreviewDialog
          open={recipientPreviewOpen}
          onClose={() => setRecipientPreviewOpen(false)}
          campaignId={id}
          channel={channel}
        />
      )}
    </Box>
  );
}
